import * as XLSX from 'xlsx';

type FieldKey = 'name' | 'dateOfBirth' | 'phone' | 'email' | 'gender' | 'weight' | 'height';

interface FieldDefinition {
    label: string;
    keywords: string[];
}

interface FieldOption {
    value: string;
    label: string;
}

interface ColumnMapping {
    index: number;
    originalHeader: string;
    detectedField: string;
}

interface ParsedTable {
    headers: string[];
    rows: string[][];
}

interface ParseFileResult {
    rawRows: string[][];
    columnMappings: ColumnMapping[];
    totalRows: number;
}

type ImportedUser = { id: number } & Record<FieldKey, string>;

// Field definitions with keywords for auto-detection
export const FIELD_DEFINITIONS: Record<FieldKey, FieldDefinition> = {
    name: {
        label: 'שם',
        keywords: ['name', 'שם', 'full name', 'שם מלא', 'שם פרטי', 'first name', 'last name', 'שם משפחה']
    },
    dateOfBirth: {
        label: 'תאריך לידה',
        keywords: ['date of birth', 'תאריך לידה', 'birth', 'לידה', 'birthdate', 'dob']
    },
    phone: {
        label: 'טלפון',
        keywords: ['phone', 'טלפון', 'mobile', 'נייד', 'מספר טלפון', 'tel', 'telephone', 'cell']
    },
    email: {
        label: 'אימייל',
        keywords: ['email', 'אימייל', 'מייל', 'דואר אלקטרוני', 'mail', 'e-mail']
    },
    gender: {
        label: 'מין',
        keywords: ['gender', 'מין', 'sex']
    },
    weight: {
        label: 'משקל',
        keywords: ['weight', 'משקל', 'kg', 'קילו']
    },
    height: {
        label: 'גובה',
        keywords: ['height', 'גובה', 'cm', 'ס"מ', 'סנטימטר']
    }
};

export const FIELD_OPTIONS: FieldOption[] = [
    { value: 'unknown', label: 'לא ידוע' },
    { value: 'ignore', label: 'התעלם' },
    { value: 'name', label: 'שם' },
    { value: 'dateOfBirth', label: 'תאריך לידה' },
    { value: 'phone', label: 'טלפון' },
    { value: 'email', label: 'אימייל' },
    { value: 'gender', label: 'מין' },
    { value: 'weight', label: 'משקל' },
    { value: 'height', label: 'גובה' }
];

// Detect field type from header text
const detectFieldFromHeader = (header: unknown): string => {
    if (!header || typeof header !== 'string') return 'unknown';

    const normalizedHeader = header.toLowerCase().trim();

    for (const [fieldKey, fieldDef] of Object.entries(FIELD_DEFINITIONS)) {
        for (const keyword of fieldDef.keywords) {
            if (normalizedHeader.includes(keyword.toLowerCase())) {
                return fieldKey;
            }
        }
    }

    return 'unknown';
};

// Detect field type from data pattern (fallback)
const detectFieldFromData = (values: string[]): string => {
    if (!values || values.length === 0) return 'unknown';

    const sampleValues = values.slice(0, 10).filter(v => v && String(v).trim());
    if (sampleValues.length === 0) return 'unknown';

    // Check for email pattern
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailMatches = sampleValues.filter(v => emailPattern.test(String(v))).length;
    if (emailMatches >= sampleValues.length * 0.5) return 'email';

    // Check for phone pattern (mostly digits, 9-12 chars)
    const phonePattern = /^[\d\s\-+()]{9,15}$/;
    const phoneMatches = sampleValues.filter(v => phonePattern.test(String(v).replace(/\s/g, ''))).length;
    if (phoneMatches >= sampleValues.length * 0.5) return 'phone';

    // Check for gender
    const genderKeywords = ['זכר', 'נקבה', 'male', 'female', 'm', 'f'];
    const genderMatches = sampleValues.filter(v =>
        genderKeywords.some(g => String(v).toLowerCase().includes(g))
    ).length;
    if (genderMatches >= sampleValues.length * 0.5) return 'gender';

    // Check for weight (number, typically 30-200)
    const weightMatches = sampleValues.filter(v => {
        const num = parseFloat(String(v));
        return !isNaN(num) && num >= 20 && num <= 250;
    }).length;
    if (weightMatches >= sampleValues.length * 0.7) return 'weight';

    // Check for date pattern
    const datePattern = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/;
    const dateMatches = sampleValues.filter(v => datePattern.test(String(v))).length;
    if (dateMatches >= sampleValues.length * 0.5) return 'dateOfBirth';

    return 'unknown';
};

// Parse CSV content
const parseCSV = (content: string): ParsedTable => {
    const lines = content.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    // Detect delimiter (comma, tab, or semicolon)
    const firstLine = lines[0];
    let delimiter = ',';
    if (firstLine.includes('\t') && !firstLine.includes(',')) {
        delimiter = '\t';
    } else if (firstLine.includes(';') && !firstLine.includes(',')) {
        delimiter = ';';
    }

    const parseRow = (line: string): string[] => {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === delimiter && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
        result.push(current.trim());

        return result;
    };

    const allRows = lines.map(parseRow);

    // Check if first row looks like headers
    // To be considered a header row, the cell value should ONLY be a header keyword,
    // not data that happens to contain a keyword (like an email containing "mail")
    const firstRow = allRows[0];
    const headerMatchCount = firstRow.filter(cell => {
        const normalized = String(cell).toLowerCase().trim();
        // Skip empty cells
        if (!normalized) return false;
        // Check if the cell value IS a keyword (not just contains it)
        return Object.values(FIELD_DEFINITIONS).some(def =>
            def.keywords.some(k => {
                const keyword = k.toLowerCase();
                // Must be an exact match or the cell is just the keyword with minor variations
                return normalized === keyword ||
                       normalized === keyword + ':' ||
                       normalized.split(/\s+/).includes(keyword);
            })
        );
    }).length;

    // Consider it a header row if at least 2 cells match keywords
    // This prevents false positives from data containing keywords
    const hasHeaders = headerMatchCount >= 2;

    if (hasHeaders) {
        return {
            headers: firstRow,
            rows: allRows.slice(1)
        };
    }

    return {
        headers: [],
        rows: allRows
    };
};

// Convert Excel serial date to dd/MM/yyyy format
const excelSerialToDate = (serial: number): string => {
    if (typeof serial !== 'number' || isNaN(serial)) return String(serial || '');

    // Excel serial date: days since January 1, 1900 (with a bug for 1900 leap year)
    // Only convert if it looks like a reasonable date (between 1950 and 2100)
    if (serial < 18264 || serial > 73050) return String(serial); // Outside 1950-2100 range

    const excelEpoch = new Date(1899, 11, 30); // Excel's epoch (Dec 30, 1899)
    const date = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
};

// Check if a value looks like an Excel serial date number
const isExcelSerialDate = (value: unknown): value is number => {
    if (typeof value !== 'number') return false;
    // Reasonable date range: 1950 to 2100
    return value >= 18264 && value <= 73050;
};

// Parse Excel file
const parseExcel = (buffer: ArrayBuffer): ParsedTable => {
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    const jsonData = XLSX.utils.sheet_to_json<unknown[]>(worksheet, { header: 1, raw: true });
    const filteredData = jsonData.filter(row =>
        row.some(cell => cell !== undefined && cell !== null && String(cell).trim() !== '')
    );

    if (filteredData.length === 0) return { headers: [], rows: [] };

    const firstRow = filteredData[0].map(cell => String(cell || ''));

    // Check if first row looks like headers
    // To be considered a header row, the cell value should ONLY be a header keyword,
    // not data that happens to contain a keyword (like an email containing "mail")
    const headerMatchCount = firstRow.filter(cell => {
        const normalized = String(cell).toLowerCase().trim();
        // Skip empty cells
        if (!normalized) return false;
        // Check if the cell value IS a keyword (not just contains it)
        return Object.values(FIELD_DEFINITIONS).some(def =>
            def.keywords.some(k => {
                const keyword = k.toLowerCase();
                // Must be an exact match or the cell is just the keyword with minor variations
                return normalized === keyword ||
                       normalized === keyword + ':' ||
                       normalized.split(/\s+/).includes(keyword);
            })
        );
    }).length;

    // Consider it a header row if at least 2 cells match keywords
    // This prevents false positives from data containing keywords
    const hasHeaders = headerMatchCount >= 2;

    // Convert cell value, handling Excel serial dates
    const convertCell = (cell: unknown): string => {
        if (cell === undefined || cell === null) return '';
        if (isExcelSerialDate(cell)) {
            return excelSerialToDate(cell);
        }
        return String(cell);
    };

    if (hasHeaders) {
        return {
            headers: firstRow,
            rows: filteredData.slice(1).map(row => row.map(convertCell))
        };
    }

    return {
        headers: [],
        rows: filteredData.map(row => row.map(convertCell))
    };
};

// Main parse function
export const parseFile = async (file: File): Promise<ParseFileResult> => {
    const fileName = file.name.toLowerCase();
    let parsed: ParsedTable;

    if (fileName.endsWith('.csv') || fileName.endsWith('.txt')) {
        const content = await file.text();
        parsed = parseCSV(content);
    } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        const buffer = await file.arrayBuffer();
        parsed = parseExcel(buffer);
    } else {
        throw new Error('סוג קובץ לא נתמך. נא להעלות קובץ CSV או Excel.');
    }

    if (parsed.rows.length === 0) {
        throw new Error('הקובץ ריק או לא ניתן לקרוא אותו.');
    }

    // Determine max columns
    const maxColumns = Math.max(
        parsed.headers.length,
        ...parsed.rows.map(row => row.length)
    );

    // Normalize rows to have same number of columns
    const normalizedRows = parsed.rows.map(row => {
        const normalized = [...row];
        while (normalized.length < maxColumns) {
            normalized.push('');
        }
        return normalized;
    });

    // Normalize headers to have same number of columns
    const normalizedHeaders = [...parsed.headers];
    while (normalizedHeaders.length < maxColumns) {
        normalizedHeaders.push('');
    }

    // Generate column mappings
    const columnMappings: ColumnMapping[] = [];
    for (let i = 0; i < maxColumns; i++) {
        let detectedField = 'unknown';
        const headerValue = normalizedHeaders[i]?.trim() || '';

        // Try to detect from header first
        if (headerValue) {
            detectedField = detectFieldFromHeader(headerValue);
        }

        // If not detected from header (or unknown), try from data patterns
        if (detectedField === 'unknown') {
            const columnValues = normalizedRows.map(row => row[i]);
            const dataDetected = detectFieldFromData(columnValues);
            if (dataDetected !== 'unknown') {
                detectedField = dataDetected;
            }
        }

        columnMappings.push({
            index: i,
            originalHeader: headerValue || `עמודה ${i + 1}`,
            detectedField
        });
    }

    return {
        rawRows: normalizedRows,
        columnMappings,
        totalRows: normalizedRows.length
    };
};

// Transform raw data based on column mappings to user objects
export const transformToUsers = (rawRows: string[][], columnMappings: ColumnMapping[]): ImportedUser[] => {
    return rawRows.map((row, rowIndex) => {
        const user: ImportedUser = {
            id: rowIndex,
            name: '',
            dateOfBirth: '',
            phone: '',
            email: '',
            gender: '',
            weight: '',
            height: ''
        };

        columnMappings.forEach(mapping => {
            if (mapping.detectedField && mapping.detectedField !== 'ignore') {
                const value = row[mapping.index] || '';
                user[mapping.detectedField as FieldKey] = value;
            }
        });

        return user;
    });
};
